import { IList, useStreamEffect } from 'annotation-tools/utils';
import React, { memo } from 'react';
import { useValueAsDataStream } from '../../utils/use-value-as-data-stream';
import { annotationCanvasStyle } from './constants/annotation-canvas-styles';
import { removeAllConfirmationMessage } from './constants/messages/remove-all-confirmation-message';
import { useEventStreams } from './hooks/use-event-streams';
import { useExtractedValues } from './hooks/use-extracted-values';
import { useSideEffects } from './hooks/use-side-effects';
import { useStreams } from './hooks/use-streams';
import {
  defaultIdMaker,
  IAnnotationCanvasStyle,
  IBboxType,
  ILabelType
} from './imports/annotation-canvas';
import {
  ButtonStateType,
  filterValue,
  get,
  IImageType,
  IRectType,
  mapToConst,
  useValueAsStream
} from './imports/utils';
import { RectToolWithCropMainView } from './rect-tool-with-crop-main-view';
import { AppMode } from './types/enum/app-mode';

// import { ParentContext } from './context/parent';

const noop = () => undefined;

const styles = IAnnotationCanvasStyle(annotationCanvasStyle);

export const RectToolWithCropMainSub = memo(
  ({
    image: imageInput,
    bboxRelativeList: bboxRelativeListInput,
    cropRectRelative: cropRectRelativeInput,
    labelList: labelListInput,
    currentPageIndex: currentPageIndexInput,
    pageLength,
    onPageIndexChange = noop,
    onBboxesRelativeChange = noop,
    onCropRectRelativeChange: onCropRectRelativeChangeInput = noop,
    onInferClick = noop,
    openSettingsDialog = noop,
    onBackToListPageClick = noop,
    navButtonsIsDisabled,
    inferButtonState = 'hide',
    openSettingsDialogButtonState = 'hide',
    onAppModeChange: onAppModeChangeInput = noop
  }: Readonly<{
    image: IImageType;
    bboxRelativeList: IList<IBboxType>;
    cropRectRelative: IRectType;
    labelList: IList<ILabelType>;
    currentPageIndex: number;
    pageLength: number;
    onPageIndexChange?: (v: number) => void;
    onBboxesRelativeChange?: (v: IList<IBboxType>) => void;
    onCropRectRelativeChange?: (v: IRectType) => void;
    onInferClick?: () => void;
    openSettingsDialog?: () => void;
    onBackToListPageClick?: () => void;
    navButtonsIsDisabled: {
      firstPage: boolean;
      prevPage: boolean;
      nextPage: boolean;
      lastPage: boolean;
    };
    inferButtonState?: ButtonStateType;
    openSettingsDialogButtonState?: ButtonStateType;
    onAppModeChange?: (appMode: AppMode) => void;
  }>) => {
    /* props */
    const image$ = useValueAsStream(imageInput);
    const cropRectRelativeInput$ = useValueAsDataStream(cropRectRelativeInput);
    const bboxRelativeListInput$ = useValueAsDataStream(bboxRelativeListInput);

    const currentPageIndexInput$ = useValueAsStream(currentPageIndexInput);
    const pageLength$ = useValueAsStream(pageLength);

    const labelListInput$ = useValueAsDataStream(labelListInput);

    /* contexts */
    // const parentInfo = React.useContext(ParentContext)

    /* events */

    const {
      appModeChange$,
      onAppModeChange,
      /* button on click */
      toolbarButtonClick$,
      onToolbarButtonClick,
      pageChange$,
      onPageChange,
      labelClick$,
      onLabelClick,
      /* labels */
      visibilityClick$,
      onVisibilityClick,
      showAllClick$,
      onShowAllClick,
      hideAllClick$,
      onHideAllClick,
      /* dialogs */
      closeHelpDialog$,
      closeHelpDialog,
      confirmDialogResult$,
      closeConfirmDialog,
      /* annotation */
      bboxesFromCanvasRelativeToImage$,
      onBboxesFromCanvasRelativeToImageChange,
      /* crop */
      cropRectRelativeFromCanvas$,
      onCropRectRelativeChange
    } = useEventStreams();

    useStreamEffect(labelListInput$, () => {
      onAppModeChange('crop-image');
    });

    useStreamEffect(pageChange$, () => {
      onAppModeChange('crop-image');
    });

    /* streams */

    const {
      currentPageIndex$,
      bboxRelativeList$,
      /* cropper */
      cropRectRelative$,
      /* annotation tool state */
      selectedLabel$,
      visibleLabels$,
      appMode$,
      /* annotation tool action */
      action$,
      /* dialogs */
      helpDialogIsOpen$,
      confirmDialogIsOpen$
    } = useStreams(
      bboxRelativeListInput$,
      cropRectRelativeInput$,
      labelListInput$,
      currentPageIndexInput$,
      pageLength$,
      appModeChange$,
      toolbarButtonClick$,
      pageChange$,
      labelClick$,
      visibilityClick$,
      showAllClick$.pipe(mapToConst('show-all')),
      hideAllClick$.pipe(mapToConst('hide-all')),
      closeHelpDialog$,
      confirmDialogResult$,
      bboxesFromCanvasRelativeToImage$,
      cropRectRelativeFromCanvas$
    );

    /* extracted values */

    const {
      bboxesRelativeToImage,
      image,
      cropRectRelative,
      selectedLabel,
      visibleLabels,
      currentPageIndex,
      helpDialogIsOpen,
      confirmDialogIsOpen,
      appMode
    } = useExtractedValues(
      bboxRelativeList$.pipe(get('bboxList')),
      image$,
      cropRectRelative$.pipe(get('cropRect')),
      selectedLabel$,
      visibleLabels$,
      currentPageIndex$,
      helpDialogIsOpen$,
      confirmDialogIsOpen$,
      appMode$
    );

    useSideEffects(
      bboxRelativeList$,
      onBboxesRelativeChange,
      currentPageIndex$,
      onPageIndexChange,
      cropRectRelative$,
      onCropRectRelativeChangeInput,
      toolbarButtonClick$.pipe(filterValue('infer')),
      onInferClick,
      toolbarButtonClick$.pipe(filterValue('settings')),
      openSettingsDialog,
      toolbarButtonClick$.pipe(filterValue('back-to-list')),
      onBackToListPageClick,
      appMode$,
      onAppModeChangeInput
    );

    return (
      <RectToolWithCropMainView
        image={image}
        /* crop */
        cropRectRelative={cropRectRelative}
        onCropRectRelativeChange={onCropRectRelativeChange}
        /* configs */
        styles={styles}
        labels={labelListInput}
        idMaker={defaultIdMaker}
        /* input */
        appMode={appMode}
        onAppModeChange={onAppModeChange}
        action$={action$}
        bboxesRelativeToImage={bboxesRelativeToImage}
        onBboxesRelativeToImageChange={onBboxesFromCanvasRelativeToImageChange}
        /* button on click */
        onToolbarButtonClick={onToolbarButtonClick}
        onPageChange={onPageChange}
        onLabelClick={onLabelClick}
        onVisibilityClick={onVisibilityClick}
        onShowAllClick={onShowAllClick}
        onHideAllClick={onHideAllClick}
        /* labels */
        visibleLabels={visibleLabels}
        selectedLabel={selectedLabel}
        /* dialogs */
        helpDialogIsOpen={helpDialogIsOpen}
        closeHelpDialog={closeHelpDialog}
        removeAllConfirmationMessage={removeAllConfirmationMessage}
        confirmDialogIsOpen={confirmDialogIsOpen}
        closeConfirmDialog={closeConfirmDialog}
        /* page */
        currentPageIndex={currentPageIndex}
        pageLength={pageLength}
        navButtonsIsDisabled={navButtonsIsDisabled}
        inferButtonState={inferButtonState}
        openSettingsDialogButtonState={openSettingsDialogButtonState}
      />
    );
  }
);

RectToolWithCropMainSub.displayName = 'RectToolWithCropMainSub';
